"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog10 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

const Blog10 = () => {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
     <Fullpage>
     <PageBackground>
    <BlogContainer>
      <MainContent>
        <HeaderSection>
          <BackButtonWrapper>
            <BackButton onClick={handleBackClick} />
          </BackButtonWrapper>
          <BlogHeader
            title="Smart Forms: Simplifying Form Completion for Students, Faculty, and Staff"
            date="17 JULY 2025"
            author="POSTED BY CAMPUS INSIGHTS"
          />
        </HeaderSection>

        <HeroImage
          src={blog10}
          alt="Smart Forms feature illustration"
        />

        <BlogContent />
      </MainContent>
    </BlogContainer>
    <OtherBlogs currentBlogId={10} />
    </PageBackground>
    </Fullpage>
    <CTASection />
  </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          In higher education, forms are everywhere—applications, financial aid requests, advising appointments, internship agreements, housing requests, and more. While these documents are necessary for keeping operations organized and compliant, they can also create significant friction. Students may find themselves repeatedly entering the same personal details across multiple forms. Staff may spend hours retyping, correcting, or chasing missing information. The result? Frustration for everyone, slower processes, and even missed opportunities.
        </IntroParagraph>
        <IntroParagraph>
          The Campus Smart Forms feature in Campus Insights solves this problem by making form completion fast, accurate, and effortless.
        </IntroParagraph>
      </IntroSection>

      <ContentSection title="What Is Campus Smart Forms?">
        <ContentParagraph>
          Campus Smart Forms is a smart, secure automation tool that integrates with your existing forms—both web-based and PDF (including Adobe-based forms). It automatically populates fields with known information, drawing from your institution's secure student information system (SIS) or other authorized data sources. Instead of manually typing name, student ID, address, and other repeated details, users can have them filled in instantly, reducing time and errors.
        </ContentParagraph>
        <ContentParagraph>
          This feature doesn't just work for students—it's equally powerful for faculty, advisors, and administrative staff. For example, faculty can quickly complete standardized reporting forms, or HR can pre-fill onboarding paperwork for new hires.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="How It Works">
        <ContentParagraph>
          When a student, staff member, or faculty member opens a supported form, Campus Smart Forms identifies the required fields and matches them with existing data from your authorized records. It fills in the details automatically, leaving only unique or situational fields—like a specific date or special request—for manual input.
        </ContentParagraph>
        <ContentParagraph>
          The system works seamlessly with Adobe PDF forms, online applications, and custom campus web forms. All data transfers are encrypted, and only approved information is used, ensuring privacy and compliance with FERPA and other relevant regulations.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Benefits for Higher Education Institutions">
        <BenefitsList>
          <BenefitItem>
            <ContentParagraph>
              Time Savings: Students spend less time filling out repetitive fields; staff spend less time processing incomplete or incorrect forms.
            </ContentParagraph>
          </BenefitItem>
          <BenefitItem>
            <ContentParagraph>
              Error Reduction: Fewer typos and mismatches mean fewer processing delays.
            </ContentParagraph>
          </BenefitItem>
          <BenefitItem>
            <ContentParagraph>
              Better User Experience: Students feel supported and guided rather than burdened by administrative tasks.
            </ContentParagraph>
          </BenefitItem>
          <BenefitItem>
            <ContentParagraph>
              Operational Efficiency: Departments process requests and applications faster, freeing resources for higher-value activities.
            </ContentParagraph>
          </BenefitItem>
          <BenefitItem>
            <ContentParagraph>
              Consistency Across Documents: Ensure uniform formatting and accuracy for institutional records.
            </ContentParagraph>
          </BenefitItem>
        </BenefitsList>
      </ContentSection>

      <ContentSection title="Real-World Examples">
        <ExamplesList>
          <ExampleItem>
            <ContentParagraph>
              A student applying for multiple scholarships can complete applications in minutes instead of hours
            </ContentParagraph>
          </ExampleItem>
          <ExampleItem>
            <ContentParagraph>
              Academic advisors can process course override requests with accurate student details pre-filled.
            </ContentParagraph>
          </ExampleItem>
          <ExampleItem>
            <ContentParagraph>
              Housing departments can have lease agreements ready with all standard student information already inserted.
            </ContentParagraph>
          </ExampleItem>
        </ExamplesList>
      </ContentSection>

      <ContentSection title="Why It Matters">
        <ContentParagraph>
          The more time students and staff spend wrestling with forms, the less time they have for meaningful engagement. Campus Smart Forms removes a persistent source of friction from campus life, replacing it with speed, accuracy, and ease. This isn't just about convenience—it's about supporting student success and institutional efficiency.
        </ContentParagraph>
        <ContentParagraph>
          With Campus Smart Forms, forms go from being a burden to being a breeze. It's one more way Campus Insights turns everyday processes into smooth, streamlined experiences that keep your campus running at its best.
        </ContentParagraph>
      </ContentSection>
    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ExamplesList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ExampleItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

export default Blog10;
